import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "zed_token";
const REFRESH_KEY = "zed_refresh";

export const getToken = () => sessionStorage.getItem(ACCESS_KEY);
export const setToken = (t) => sessionStorage.setItem(ACCESS_KEY, t);
export const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const setRefresh = (t) => localStorage.setItem(REFRESH_KEY, t);
export const clearToken = () => {
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

let refreshPromise = null;
async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const rt = getRefresh();
  if (!rt) throw new Error("No refresh token");
  refreshPromise = axios
    .post(`${API}/admin/refresh`, { refresh_token: rt })
    .then((r) => { setToken(r.data.token); return r.data.token; })
    .catch((err) => { clearToken(); throw err; })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    if (
      status === 401 &&
      original && !original._retry &&
      !original.url?.endsWith("/admin/refresh") &&
      !original.url?.endsWith("/admin/login") &&
      getRefresh()
    ) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (e) {
        clearToken();
        return Promise.reject(err);
      }
    }
    if (status === 401 && getToken() && !getRefresh()) clearToken();
    return Promise.reject(err);
  }
);

// --- Menu ---
export const fetchMenu = async (params = {}) => {
  const { data } = await api.get("/menu", { params });
  // Guard: backend must return an array. If routing/proxy returns HTML or an
  // error object (common when /api is not forwarded to the backend on a
  // self-hosted server), fall back to [] so the UI doesn't crash.
  return Array.isArray(data) ? data : [];
};
export const trackView = async (id) => { try { await api.post(`/menu/${id}/view`); } catch (_) {} };
export const fetchAdminMenu = async () => (await api.get("/admin/menu")).data;
export const createItem = async (p) => (await api.post("/menu", p)).data;
export const updateItem = async (id, p) => (await api.put(`/menu/${id}`, p)).data;
export const deleteItem = async (id) => (await api.delete(`/menu/${id}`)).data;
export const fetchAnalytics = async () => (await api.get("/menu/analytics")).data;

// --- Auth ---
export const adminLogin = async (email, password) => {
  const { data } = await api.post("/admin/login", { email, password });
  setToken(data.token);
  if (data.refresh_token) setRefresh(data.refresh_token);
  return data;
};
export const adminMe = async () => (await api.get("/admin/me")).data;
export const forgotPassword = async (email) => (await api.post("/admin/forgot-password", { email })).data;
export const resetPassword = async (token, password) => (await api.post("/admin/reset-password", { token, password })).data;

// --- Audit ---
export const fetchAuditLogs = async (params = {}) => (await api.get("/audit-logs", { params })).data;

// --- Upload ---
export const uploadImage = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
  return { ...data, url: `${BACKEND_URL}${data.relative_url}` };
};
export const cleanupFiles = async () => (await api.post("/admin/files/cleanup")).data;

// --- Categories ---
export const fetchCategories = async (activeOnly = true) => {
  const { data } = await api.get("/categories", { params: { active_only: activeOnly } });
  return Array.isArray(data) ? data : [];
};
export const createCategory = async (p) => (await api.post("/categories", p)).data;
export const updateCategory = async (id, p) => (await api.put(`/categories/${id}`, p)).data;
export const deleteCategory = async (id) => (await api.delete(`/categories/${id}`)).data;
export const reorderCategories = async (ids) => (await api.post("/categories/reorder", { ids })).data;

export const fetchPromotions = async (activeOnly = true) => {
  const { data } = await api.get("/promotions", { params: { active_only: activeOnly } });
  return Array.isArray(data) ? data : [];
};
export const createPromotion = async (p) => (await api.post("/promotions", p)).data;
export const updatePromotion = async (id, p) => (await api.put(`/promotions/${id}`, p)).data;
export const deletePromotion = async (id) => (await api.delete(`/promotions/${id}`)).data;
export const reorderPromotions = async (ids) => (await api.post("/promotions/reorder", { ids })).data;

// --- Users ---
export const fetchUsers = async () => (await api.get("/users")).data;
export const createUser = async (p) => (await api.post("/users", p)).data;
export const updateUser = async (id, p) => (await api.put(`/users/${id}`, p)).data;
export const deleteUser = async (id) => (await api.delete(`/users/${id}`)).data;

// --- Settings ---
export const fetchSettings = async () => (await api.get("/settings")).data;
export const updateSettings = async (p) => (await api.put("/settings", p)).data;

// Helper to build a full public URL from a relative /api/files path or return as-is
export const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

// Safely turn any API error (string, FastAPI 422 array, or object) into a
// readable string so we never pass an object to a React node (white-screen crash).
export const getErrorMessage = (err, fallback = "Error") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => (typeof e === "string" ? e : e?.msg || JSON.stringify(e))).join(", ");
  }
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return err?.response?.data?.message || err?.message || fallback;
};
